// react
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import '@fortawesome/fontawesome-free/css/all.min.css';

import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fade,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import {
  useLazyGetEnergyConsumptionQuery,
  useLazyGetSeuEnergyConsumptionQuery,
} from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import { AddButton } from '@/components/ui/button';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import AnalysisAggregateMenu from '@/features/energyxplorer/components/analysis/AnalysisAggregateMenu';
import { AnalysisViewToggleButtonGroup } from '@/features/energyxplorer/components/analysis/AnalysisViewToggleButtonGroup';
import EnergyAnalysisSeuSingleSeriesChart from '@/features/energyxplorer/components/analysis/EnergyAnalysisSeuSingleSeriesChart';
import EnergyAnalysisSingleSeriesChart from '@/features/energyxplorer/components/analysis/EnergyAnalysisSingleSeriesChart';
import EnergyAnalyzerFilterDialog from '@/features/energyxplorer/components/analysis/EnergyAnalyzerFilterDialog';
import { TimeAggregateButtonGroup } from '@/features/energyxplorer/components/analysis/TimeAggregateButtonGroup';
import HighlightControls from '@/features/energyxplorer/components/HighlightControls';
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
import { useAggregationParam } from '@/features/energyxplorer/hooks/useAggregationParam';
import useChartHighlightParams from '@/features/energyxplorer/hooks/useChartHighlightParams';
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import { useSeriesFiltersParam } from '@/features/energyxplorer/hooks/useSeriesFilterParam';
import { useTimeAggregate } from '@/features/energyxplorer/hooks/useTimeAggregate';
import useUrlAnalysisScope from '@/features/energyxplorer/hooks/useUrlAnalysisScope';
import useUrlSeu from '@/features/energyxplorer/hooks/useUrlSeu';
import useUrlViewMode from '@/features/energyxplorer/hooks/useUrlViewMode';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

import { DateTime } from 'luxon';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

function shouldSkipQuery({ filters, selectedEnergyGroup, energyTreeNode }) {
  if (!energyTreeNode || !selectedEnergyGroup) return true;

  const hasActivePeriodFilter =
    filters &&
    Object.prototype.hasOwnProperty.call(filters, 'activePeriodFilter');

  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio') === true;

  const hasOpeningHours = !!energyTreeNode?.options?.openingHours;

  // We SKIP when portfolio nodes lack opening hours AND no activeDaysFilter
  return isPortfolio && !hasOpeningHours && hasActivePeriodFilter;
}

const getFilterLabel = (type, filters, t) => {
  const value = filters?.[type];

  switch (type) {
    case 'activePeriodFilter':
      switch (value) {
        case true:
          return t('filter_labels.active_period');
        case false:
          return t('filter_labels.inactive_period');
        default:
          return t('filter_labels.all_periods');
      }

    case 'activeHolidaysFilter':
      switch (value) {
        case true:
          return t('filter_labels.holidays_included');
        case false:
          return t('filter_labels.holidays_excluded');
        default:
          return '';
      }

    default:
      return '';
  }
};

/**
 * IMPORTANT:
 * Adjust this predicate to your actual API response shape.
 * The goal is: return true only when the response contains at least one datapoint.
 */
const hasMeaningfulData = (res) => {
  if (!res) return false;

  // Common possible shapes. Keep this permissive but not "always true".
  // 1) direct arrays
  if (Array.isArray(res.data)) return res.data.length > 0;
  if (Array.isArray(res.points)) return res.points.length > 0;
  if (Array.isArray(res.values)) return res.values.length > 0;
  if (Array.isArray(res.items)) return res.items.length > 0;

  // 2) series arrays (Highcharts-like or API series)
  if (Array.isArray(res.series)) {
    // series might be [{ data: [...] }, ...]
    return res.series.some((s) => (s?.data?.length ?? 0) > 0);
  }

  // 3) nested known shapes
  if (Array.isArray(res.result)) return res.result.length > 0;
  if (Array.isArray(res.result?.data)) return res.result.data.length > 0;
  if (Array.isArray(res.result?.series)) {
    return res.result.series.some((s) => (s?.data?.length ?? 0) > 0);
  }

  // If we cannot detect shape, treat as "has data" to avoid false dimming.
  // If you paste a sample response, we can remove this fallback.
  return true;
};

/**
 * Extracted subcomponent #2: SeriesList
 */
const SeriesList = ({
  seriesColors = [],
  filterObjects = [],
  availableSeriesIds = new Set(),
  onRemoveSeries,
  t,
  addSeriesButtonProps,
  infoPlacement = 'right',
}) => {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" color="textSecondary" fontWeight="bold">
            {t('common.series')}
          </Typography>
          <Tooltip
            title={t('consumption_analysis.series_description')}
            placement={infoPlacement}
          >
            <InfoRoundedIcon
              fontSize="small"
              sx={{
                color: (theme) =>
                  theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                ml: 1,
              }}
            />
          </Tooltip>
        </Box>

        <AddButton
          onClick={addSeriesButtonProps?.onClick}
          {...(addSeriesButtonProps || {})}
        >
          {t('consumption_analysis.add_series')}
        </AddButton>
      </Box>

      <List dense>
        {filterObjects.map((filter, index) => {
          // IMPORTANT: availableSeriesIds must contain filter indices (numbers)
          const hasData = availableSeriesIds.has(index);

          // Defensive: seriesColors may be sparse array aligned by filterIndex
          const seriesColor = seriesColors?.[index]?.color ?? '#ccc';

          const usedFiltersText = [
            ['activePeriodFilter', filter?.activePeriodFilter],
            ['activeHolidaysFilter', filter?.activeHolidaysFilter],
          ]
            .map(([key]) => getFilterLabel(key, filter, t))
            .filter(Boolean);

          return (
            <ListItem
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                mb: 1,
                alignItems: 'center',
                opacity: hasData ? 1 : 0.45,
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  onClick={() => onRemoveSeries(index)}
                  sx={{ opacity: 1 }}
                >
                  <DeleteRoundedIcon />
                </IconButton>
              }
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: seriesColor,
                  mr: 2,
                }}
              />

              <ListItemText
                primary={
                  <Typography variant="caption" component="div">
                    {usedFiltersText.length ? (
                      usedFiltersText.map((text, i) => (
                        <span key={i}>
                          {i > 0 && ', '}
                          <strong>{text}</strong>
                        </span>
                      ))
                    ) : (
                      <span>{t('child_analysis.no_filters_applied')}</span>
                    )}

                    {!hasData && (
                      <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('analysis.no_data_for_series', {
                            ns: 'energyxplorer',
                          })}
                        </Typography>
                      </Box>
                    )}
                  </Typography>
                }
                // secondary={
                //   <Typography variant="caption" component="div">
                //     <i>{t('child_analysis.used_filters')}: </i>
                //     {usedFiltersText.length ? (
                //       usedFiltersText.map((text, i) => (
                //         <span key={i}>
                //           {i > 0 && ', '}
                //           <strong>{text}</strong>
                //         </span>
                //       ))
                //     ) : (
                //       <span>{t('child_analysis.no_filters_applied')}</span>
                //     )}

                //     {!hasData && (
                //       <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                //         <Typography
                //           variant="caption"
                //           sx={{ color: 'text.secondary' }}
                //         >
                //           {t('analysis.no_data_for_series', {
                //             ns: 'energyxplorer',
                //           })}
                //         </Typography>
                //       </Box>
                //     )}
                //   </Typography>
                // }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

/**
 * Extracted subcomponent #3: Side Panel
 */
const AnalysisSidePanel = ({
  analysisMode,
  t,
  availableSeriesIds,
  seriesColors,
  filterObjects,
  removeSeriesAt,
  onAddSeries,
  filtersOpen,
}) => {
  const showNodeMode = analysisMode === 'node';
  const showSeuMode = analysisMode === 'seu';

  return (
    <Grid
      className="non-printable"
      sx={{ transition: 'flex-basis 0.3s ease' }}
      size={{
        md: 12,
        lg: 3
      }}>
      <Box
        sx={{
          maxHeight: filtersOpen ? 1000 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Card>
              <CardContent>
                {showSeuMode && (
                  <SeriesList
                    availableSeriesIds={availableSeriesIds}
                    seriesColors={seriesColors}
                    filterObjects={filterObjects}
                    onRemoveSeries={removeSeriesAt}
                    t={t}
                    addSeriesButtonVariant="contained"
                    addSeriesButtonProps={{ onClick: onAddSeries }}
                    infoPlacement="left"
                  />
                )}

                {showNodeMode && (
                  <SeriesList
                    availableSeriesIds={availableSeriesIds}
                    seriesColors={seriesColors}
                    filterObjects={filterObjects}
                    onRemoveSeries={removeSeriesAt}
                    t={t}
                    addSeriesButtonVariant="text"
                    addSeriesButtonProps={{ onClick: onAddSeries }}
                    infoPlacement="right"
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size="grow">
            <EnergyTreeNodeTraversal showModeToggle={true} />
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

const EnergyTreeNodeAnalysisPage = () => {
  const { t } = useTranslation([
    'energyxplorer-analysis-tabs',
    'energyxplorer',
  ]);

  const { accountId, energyGroupId } = useParams({ strict: false });
  const { timeRange } = useUrlDateRange();
  const { nodeId, setNodeId, energyTreeNode } = useEnergyTreeNode();

  const selectedRange = useMemo(
    () => [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ],
    [timeRange],
  );

  const [aggregate] = useTimeAggregate();

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  const { aggregation } = useAggregationParam('aggregation');

  const dispatch = useDispatch();

  const {
    series: filterObjects,
    removeSeriesAt,
    ensureDefaultSeries,
  } = useSeriesFiltersParam('series');

  useLayoutEffect(() => {
    if (!nodeId) return;
    ensureDefaultSeries([{}]);
  }, [nodeId, ensureDefaultSeries]);

  const [viewMode] = useUrlViewMode({
    allowed: ['column', 'line'],
    fallback: 'column',
  });

  const [analysisScope] = useUrlAnalysisScope({
    allowed: ['node', 'seu'],
    fallback: 'node',
  });

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  // IMPORTANT: separate colors for node vs SEU to avoid cross-scope "drift"
  const [nodeSeriesColors, setNodeSeriesColors] = useState([]);
  const [seuSeriesColors, setSeuSeriesColors] = useState([]);

  const [energyConsumptionDataSeries, setEnergyConsumptionDataSeries] =
    useState([]);
  const [seuEnergyConsumptionDataSeries, setSeuEnergyConsumptionDataSeries] =
    useState([]);

  const { highlightEnabled, highlightRange, toggleHighlight, clearHighlight } =
    useChartHighlightParams();

  const nodeReqIdRef = useRef(0);
  const seuReqIdRef = useRef(0);

  const [
    getEnergyConsumptionData,
    {
      isLoading: isLoadingEnergyConsumptionData,
      isFetching: isFetchingEnergyConsumptionData,
      isUninitialized: isUninitializedEnergyConsumptionData,
    },
  ] = useLazyGetEnergyConsumptionQuery();

  const [
    getSeuEnergyConsumptionData,
    {
      isLoading: isLoadingSeuEnergyConsumptionData,
      isFetching: isFetchingSeuEnergyConsumptionData,
      isUninitialized: isUninitializedSeuEnergyConsumptionData,
    },
  ] = useLazyGetSeuEnergyConsumptionQuery();

  const fetchEnergyConsumption = useCallback(
    async ({ start, end, filters }) => {
      return await getEnergyConsumptionData({
        accountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId,
        aggregate,
        start,
        end,
        ...filters,
      }).unwrap();
    },
    [
      getEnergyConsumptionData,
      accountId,
      selectedEnergyGroup,
      nodeId,
      aggregate,
    ],
  );

  const fetchSeuEnergyConsumption = useCallback(
    async ({ start, end, filters, seu }) => {
      return await getSeuEnergyConsumptionData({
        accountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        seu,
        aggregate,
        start,
        end,
        ...filters,
      }).unwrap();
    },
    [accountId, aggregate, getSeuEnergyConsumptionData, selectedEnergyGroup],
  );

  // Default nodeId if missing
  useEffect(() => {
    if (!selectedEnergyGroup) return;

    const firstNodeId =
      selectedEnergyGroup?.energyTreeNodes?.[0]?.energyTreeNodeId;
    if (!nodeId) setNodeId(firstNodeId || null);
  }, [selectedEnergyGroup, nodeId, setNodeId]);

  // Node series fetch: CLEAR + request-id guard + "empty response => null"
  useEffect(() => {
    if (!nodeId) return;

    const resetData = async () => {
      setEnergyConsumptionDataSeries([]);
      setNodeSeriesColors([]);
    };

    const reqId = ++nodeReqIdRef.current;

    resetData();

    let cancelled = false;

    (async () => {
      try {
        const results = await Promise.all(
          filterObjects.map(async (filters, idx) => {
            const shouldSkip = shouldSkipQuery({
              filters,
              selectedEnergyGroup,
              energyTreeNode,
            });
            if (shouldSkip) return null;

            try {
              const res = await fetchEnergyConsumption({
                start: timeRange.start,
                end: timeRange.end,
                filters,
              });

              if (!hasMeaningfulData(res)) return null;

              return {
                ...res,
                metadata: { ...filters, id: idx },
              };
            } catch {
              // Error handled by apiErrorMiddleware (snackbar + App Insights).
              return null;
            }
          }),
        );

        if (cancelled) return;
        if (nodeReqIdRef.current !== reqId) return;

        setEnergyConsumptionDataSeries(results.filter(Boolean));
      } catch (error) {
        if (cancelled) return;
        if (nodeReqIdRef.current !== reqId) return;
        setEnergyConsumptionDataSeries([]);
        console.error('Error fetching energy consumption data:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    nodeId,
    timeRange.start,
    timeRange.end,
    aggregate,
    filterObjects,
    fetchEnergyConsumption,
    dispatch,
    selectedEnergyGroup,
    energyTreeNode,
  ]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (!type) return;
      if (!map.has(type)) map.set(type, []);
      map.get(type).push(node);
    });
    return map;
  }, [selectedEnergyGroup]);

  const dataSeriesAggregate = { value: aggregation };

  const validSeuOptions = Array.from(nodeGroupsByType.entries())
    .filter(([, nodes]) =>
      nodes.some((node) => (node?.dataStreams?.length ?? 0) > 0),
    )
    .map(([type]) => type)
    .filter(
      (type) =>
        type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
    );

  const [seuToAnalyse] = useUrlSeu({
    key: 'type',
    allowed: validSeuOptions,
    fallback: () => validSeuOptions[0] ?? null,
  });

  const effectiveSeu = analysisScope === 'seu' ? seuToAnalyse : null;

  // SEU series fetch: CLEAR + request-id guard + "empty response => null"
  useEffect(() => {
    if (!effectiveSeu || !selectedEnergyGroup) return;

    const reqId = ++seuReqIdRef.current;

    const resetData = async () => {
      setSeuEnergyConsumptionDataSeries([]);
      setSeuSeriesColors([]);
    };

    resetData();

    let cancelled = false;

    (async () => {
      try {
        const results = await Promise.all(
          filterObjects.map(async (filters, idx) => {
            try {
              const res = await fetchSeuEnergyConsumption({
                start: timeRange.start,
                end: timeRange.end,
                filters,
                seu: effectiveSeu,
              });

              if (!hasMeaningfulData(res)) return null;

              return {
                ...res,
                metadata: { ...filters, id: idx },
              };
            } catch {
              return null;
            }
          }),
        );

        if (cancelled) return;
        if (seuReqIdRef.current !== reqId) return;

        setSeuEnergyConsumptionDataSeries(results.filter(Boolean));
      } catch (error) {
        if (cancelled) return;
        if (seuReqIdRef.current !== reqId) return;
        setSeuEnergyConsumptionDataSeries([]);
        console.error('Error fetching SEU energy consumption data:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    effectiveSeu,
    timeRange.start,
    timeRange.end,
    aggregate,
    filterObjects,
    fetchSeuEnergyConsumption,
    selectedEnergyGroup,
  ]);

  const sidePanelSeriesColors =
    analysisScope === 'node' ? nodeSeriesColors : seuSeriesColors;

  // IMPORTANT: this is what SeriesList uses for `availableSeriesIds.has(index)`
  // Therefore it MUST contain FILTER INDICES (numbers).
  const availableSeriesIds = useMemo(() => {
    const ids = new Set();
    const seriesData =
      analysisScope === 'node'
        ? energyConsumptionDataSeries
        : seuEnergyConsumptionDataSeries;

    for (const s of seriesData ?? []) {
      const raw = s?.metadata?.id;
      const id = typeof raw === 'string' ? Number(raw) : raw;
      if (Number.isFinite(id)) ids.add(id);
    }

    return ids;
  }, [
    analysisScope,
    energyConsumptionDataSeries,
    seuEnergyConsumptionDataSeries,
  ]);

  const readyToRender = energyTreeNode && selectedEnergyGroup && filterObjects;

  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Fade in={readyToRender} timeout={500}>
          <Grid container spacing={2}>
            <Grid
              sx={{ transition: 'flex-basis 0.3s ease-in-out' }}
              size={{
                md: 12,
                lg: filtersOpen ? 9 : 12
              }}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      {analysisScope === 'node'
                        ? `${energyTreeNode?.name} (Node)`
                        : `${effectiveSeu} (SEU)`}
                      <InfoTooltipIcon
                        title={
                          t('consumption_analysis.sum_consumption_for') +
                          ' ' +
                          (analysisScope === 'node'
                            ? energyTreeNode?.name
                            : effectiveSeu)
                        }
                      />
                    </Box>
                  }
                  subheader={
                    selectedRange[0] && selectedRange[1]
                      ? `${t('child_analysis.in_period')} ${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                      : t('child_analysis.select_period')
                  }
                  action={
                    <EnergyTreeNodeAnalysisToolbar
                      filtersOpen={filtersOpen}
                      showFilterToggle
                      onToggleFilters={() => setFiltersOpen((v) => !v)}
                      filterToggleTooltip={{
                        open: t('consumption_analysis.tooltips.hide'),
                        closed: t('consumption_analysis.tooltips.show'),
                      }}
                      actions={
                        <Stack direction="row" gap={2} alignItems="center">
                          <HighlightControls
                            highlightEnabled={highlightEnabled}
                            hasHighlights={!!highlightRange}
                            onToggle={toggleHighlight}
                            onClear={clearHighlight}
                          />
                          <AnalysisAggregateMenu />
                          <AnalysisViewToggleButtonGroup />
                          <TimeAggregateButtonGroup />
                        </Stack>
                      }
                    />
                  }
                />
                <CardContent>
                  {analysisScope === 'node' && (
                    <Suspense
                      fallback={
                        <div>
                          {t('analysis.loading', { ns: 'energyxplorer' })}
                        </div>
                      }
                    >
                      <EnergyAnalysisSingleSeriesChart
                        lazyLoadedEnergyConsumptionData={
                          energyConsumptionDataSeries || []
                        }
                        isLoading={
                          isLoadingEnergyConsumptionData ||
                          isFetchingEnergyConsumptionData ||
                          isUninitializedEnergyConsumptionData
                        }
                        availableSeriesIds={availableSeriesIds}
                        viewMode={viewMode}
                        aggregate={aggregate}
                        dataSeriesAggregate={dataSeriesAggregate}
                        selectedEnergyGroup={selectedEnergyGroup}
                        setSeriesColors={setNodeSeriesColors}
                      />
                    </Suspense>
                  )}

                  {analysisScope === 'seu' && (
                    <Suspense
                      fallback={
                        <div>
                          {t('analysis.loading', { ns: 'energyxplorer' })}
                        </div>
                      }
                    >
                      <EnergyAnalysisSeuSingleSeriesChart
                        selectedEnergyGroup={selectedEnergyGroup}
                        aggregate={aggregate}
                        timeRange={timeRange}
                        data={seuEnergyConsumptionDataSeries || []}
                        dataSeriesAggregate={dataSeriesAggregate}
                        isLoading={
                          isLoadingSeuEnergyConsumptionData ||
                          isFetchingSeuEnergyConsumptionData ||
                          isUninitializedSeuEnergyConsumptionData
                        }
                        viewMode={viewMode}
                        setSeriesColors={setSeuSeriesColors}
                      />
                    </Suspense>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {filtersOpen && (
              <AnalysisSidePanel
                analysisMode={analysisScope}
                t={t}
                availableSeriesIds={availableSeriesIds}
                seriesColors={sidePanelSeriesColors}
                filterObjects={filterObjects}
                removeSeriesAt={removeSeriesAt}
                onAddSeries={() => setOpenFilterDialog(true)}
                filtersOpen={filtersOpen}
              />
            )}
          </Grid>
        </Fade>
      )}

      <EnergyAnalyzerFilterDialog
        open={openFilterDialog}
        handleClose={() => setOpenFilterDialog(false)}
        selectedEnergyGroup={selectedEnergyGroup}
        startHour={selectedEnergyGroup?.options?.workingDayStart}
        endHour={selectedEnergyGroup?.options?.workingDayEnd}
        seriesFilterParam="series"
      />
    </PageContainer>
  );
};

export default EnergyTreeNodeAnalysisPage;
